import { Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { IMAGES } from "@/data/site";
import { PageHero, SectionHeader } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Section } from "@/components/Section";
import { Editable } from "@/components/Editable";
import ProjectShowcase from "@/components/ProjectShowcase";
import { Mountain, Droplets, Recycle, ShieldCheck, ArrowUpRight, CheckCircle2 } from "lucide-react";

const HL_ICONS = [Droplets, Recycle, ShieldCheck, Mountain];

export default function Mining() {
  const { t } = useApp();
  const m = t.miningP;
  return (
    <div>
      <PageHero image={IMAGES.miningPit} eyebrow={m.eyebrow} title={t.pillars.mining.title} subtitle={t.pillars.mining.desc}>
        <Link to="/contact" className="oj-btn-gold" data-testid="mining-contact-btn">{m.offtake} <ArrowUpRight className="w-4 h-4" /></Link>
      </PageHero>

      {/* Corporate Overview — Mining & Minerals */}
      <Section sid="mining.profile" name="Mining Overview" className="oj-section bg-[#E9F5F4]/40 border-b border-teal-100" data-testid="mining-profile">
        <div className="oj-container grid grid-cols-1 lg:grid-cols-3 gap-10">
          <Reveal className="lg:col-span-1">
            <div className="lg:sticky lg:top-28">
              <div className="oj-eyebrow mb-3"><Mountain className="w-4 h-4" /> {m.corpOverview}</div>
              <Editable as="h2" id="mining.profile.title" className="oj-h2 block">{m.title}</Editable>
              <div className="mt-5 h-1 w-20 rounded-full bg-gradient-to-r from-amber-400 to-amber-500" />
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-2">
            <div className="space-y-5">
              <Editable as="p" id="mining.profile.p0" className="text-slate-600 leading-relaxed block">{m.p0}</Editable>
              <div className="oj-card p-6 border-l-4 !border-l-amber-400">
                <Editable as="p" id="mining.profile.p1" className="text-[#0B3B39] font-semibold leading-relaxed block">{m.p1}</Editable>
              </div>
            </div>
          </Reveal>
        </div>
      </Section>

      <Section sid="mining.eco" name="Responsible Extraction" className="oj-section">
        <div className="oj-container grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="oj-eyebrow mb-3">{m.respExtraction}</div>
            <h2 className="oj-h2 mb-6">{m.ecoTitle}</h2>
            <p className="text-slate-600 leading-relaxed mb-6">{m.ecoPara}</p>
            <div className="space-y-4">
              {m.bullets.map((f) => (
                <div key={f} className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" /><span className="text-sm text-slate-600">{f}</span></div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="grid grid-cols-2 gap-4">
              <img src={IMAGES.miningHero} alt="mining" className="rounded-2xl h-64 w-full object-cover border border-teal-900/50" />
              <img src={IMAGES.miningTruck} alt="mining truck" className="rounded-2xl h-64 w-full object-cover border border-teal-900/50 mt-8" />
            </div>
          </Reveal>
        </div>
      </Section>

      <Section sid="mining.ops" name="Operational Excellence" className="bg-[#E9F5F4]/40 border-y border-teal-900/40 oj-section">
        <div className="oj-container">
          <SectionHeader eyebrow={m.opExcellence} title={m.opsTitle} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {m.highlights.map(([title, desc], i) => {
              const Icon = HL_ICONS[i];
              return (
                <Reveal key={title} delay={i * 0.08}>
                  <div className="oj-card p-7 flex gap-5 h-full">
                    <div className="grid place-items-center w-12 h-12 rounded-xl bg-amber-500/15 border border-amber-400/30 shrink-0"><Icon className="w-6 h-6 text-amber-400" /></div>
                    <div><h4 className="font-bold text-white mb-1.5">{title}</h4><p className="text-sm text-slate-600 leading-relaxed">{desc}</p></div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </Section>

      <ProjectShowcase
        category="mining_project"
        eyebrow={m.galleryEyebrow}
        title={m.galleryTitle}
        subtitle={m.gallerySub}
      />
    </div>
  );
}
